import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import Tag from "@/components/base/Tag";
import { detailPath, getByType, searchContent, typeMeta } from "@/lib/content";
import type { ContentType } from "@/mocks/content";

type FilterKey = "all" | ContentType;

const filters: { key: FilterKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "animation", label: "动画剧情" },
  { key: "character", label: "角色分析" },
  { key: "news", label: "动漫资讯" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const filterKey = (params.get("type") as FilterKey) ?? "all";
  const [input, setInput] = useState(keyword);

  const results = useMemo(
    () => (keyword.trim() ? searchContent(keyword, filterKey) : []),
    [keyword, filterKey]
  );

  const recommendations = useMemo(
    () => [...getByType("animation"), ...getByType("character"), ...getByType("news")].slice(0, 3),
    []
  );

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams();
    if (input.trim()) next.set("q", input.trim());
    if (filterKey !== "all") next.set("type", filterKey);
    setParams(next);
  };

  const changeFilter = (key: FilterKey) => {
    const next = new URLSearchParams(params);
    if (key === "all") next.delete("type");
    else next.set("type", key);
    setParams(next);
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-secondary-50">
        <div className="mx-auto w-full max-w-5xl px-4 pt-28 pb-10 md:px-8 md:pt-36 md:pb-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "站内搜索" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            站内搜索
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            输入关键词，检索剧情介绍、角色分析与动漫资讯的标题与正文内容。
          </p>

          <form onSubmit={submit} className="mt-6 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i
                className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"
                aria-hidden="true"
              />
              <input
                type="text"
                name="q"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="例如：昴、遗产钥匙、地下书库"
                className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-search-line" aria-hidden="true" />
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-8 md:py-10">
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="mr-1 text-xs font-medium text-foreground-400">分类筛选</span>
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => changeFilter(f.key)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                filterKey === f.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {keyword.trim() ? (
          <>
            <p className="mb-4 text-sm text-foreground-500">
              关键词「<span className="font-bold text-foreground-900">{keyword}</span>」共找到
              <span className="mx-1 font-bold text-primary-600">{results.length}</span>条结果
            </p>

            {results.length > 0 ? (
              <div className="flex flex-col gap-4">
                {results.map((item) => (
                  <Link
                    key={item.id}
                    to={detailPath(item)}
                    className="group flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 sm:flex-row"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <Tag tone="accent">{typeMeta[item.type].short}</Tag>
                        {item.tags.slice(0, 2).map((tag) => (
                          <Tag key={tag}>{tag}</Tag>
                        ))}
                      </div>
                      <h2 className="font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </h2>
                      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">
                        {item.summary}
                      </p>
                    </div>
                    <span className="flex shrink-0 items-center gap-1 self-end text-xs font-medium text-foreground-400 transition-colors group-hover:text-accent-600 sm:self-center">
                      查看详情
                      <i className="ri-arrow-right-line" aria-hidden="true" />
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
                <i className="ri-search-eye-line text-4xl text-foreground-300" aria-hidden="true" />
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">
                  没有找到相关结果
                </h3>
                <p className="mt-2 text-sm text-foreground-500">
                  试试更换关键词，或浏览下面的推荐内容。
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <i className="ri-search-line text-4xl text-foreground-300" aria-hidden="true" />
            <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">
              输入关键词开始检索
            </h3>
            <p className="mt-2 text-sm text-foreground-500">
              可以搜索角色名、剧情关键词或资讯主题。
            </p>
          </div>
        )}

        <section className="mt-12">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
            <i className="ri-fire-line text-accent-500" aria-hidden="true" />
            热门推荐
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {recommendations.map((item) => (
              <Link
                key={item.id}
                to={detailPath(item)}
                className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="text-xs text-accent-600">{typeMeta[item.type].short}</span>
                <h3 className="mt-1.5 line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                  {item.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}